import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { ArrowLeftIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { ringGroupsAPI, extensionsAPI, destinationsAPI } from '../../services/api';
import toast from 'react-hot-toast';

const RingGroupFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(isEditing);
  const [extensions, setExtensions] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [destinations, setDestinations] = useState([]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      extension: '',
      name: '',
      description: '',
      strategy: 'ringall',
      ring_time: 20,
      destination_if_no_answer: '',
    },
  });

  useEffect(() => {
    fetchExtensions();
    fetchDestinations();
    if (isEditing) {
      fetchRingGroup();
    }
  }, [id, isEditing]);

  const fetchExtensions = async () => {
    try {
      const response = await extensionsAPI.getAll();
      const data = response.data?.data || response.data || [];
      setExtensions(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch extensions:', error);
    }
  };

  const fetchDestinations = async () => {
    try {
      const response = await destinationsAPI.getAll();
      const data = response.data?.data || response.data || [];
      setDestinations(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch destinations:', error);
    }
  };

  const fetchRingGroup = async () => {
    try {
      const response = await ringGroupsAPI.getById(id);
      const data = response.data?.data || response.data;
      reset({
        extension: data.extension || '',
        name: data.name || '',
        description: data.description || '',
        strategy: data.strategy || 'ringall',
        ring_time: data.ring_time || data.ringtime || 20,
        destination_if_no_answer: data.destination_if_no_answer || '',
      });
      
      if (data.members) {
        setSelectedMembers(data.members);
      } else if (data.extension_list) {
        setSelectedMembers(data.extension_list.split('-'));
      }
    } catch (error) {
      console.error('Failed to fetch ring group:', error);
      toast.error('Failed to load ring group');
      navigate('/ring-groups');
    } finally {
      setIsFetching(false);
    }
  };

  const addMember = (extensionNumber) => {
    if (!selectedMembers.includes(extensionNumber)) {
      setSelectedMembers([...selectedMembers, extensionNumber]);
    }
  };

  const removeMember = (extensionNumber) => {
    setSelectedMembers(selectedMembers.filter(m => m !== extensionNumber));
  };

  const onSubmit = async (data) => {
    if (selectedMembers.length === 0) {
      toast.error('Please add at least one member');
      return;
    }

    setIsLoading(true);
    
    try {
      const payload = {
        ...data,
        members: selectedMembers,
        extension_list: selectedMembers.join('-'),
      };

      if (isEditing) {
        await ringGroupsAPI.update(id, payload);
        toast.success('Ring group updated successfully');
      } else {
        await ringGroupsAPI.create(payload);
        toast.success('Ring group created successfully');
      }
      navigate('/ring-groups');
    } catch (error) {
      console.error('Failed to save ring group:', error);
      toast.error(error.response?.data?.error || 'Failed to save ring group');
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl animate-fade-in">
      <div className="mb-6">
        <button
          onClick={() => navigate('/ring-groups')}
          className="flex items-center text-slate-400 hover:text-white mb-4 transition-colors"
        >
          <ArrowLeftIcon className="w-4 h-4 mr-2" />
          Back to Ring Groups
        </button>
        <h1 className="text-2xl font-bold text-white">
          {isEditing ? 'Edit Ring Group' : 'Create Ring Group'}
        </h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="card space-y-5">
          <h2 className="text-lg font-semibold text-white border-b border-slate-800 pb-3">
            Basic Information
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="label">Ring Group Number *</label>
              <input
                type="text"
                {...register('extension', { required: 'Required' })}
                disabled={isEditing}
                className={`input ${isEditing ? 'opacity-50' : ''}`}
                placeholder="600"
              />
            </div>
            <div>
              <label className="label">Name *</label>
              <input
                type="text"
                {...register('name', { required: 'Required' })}
                className="input"
                placeholder="Sales Team"
              />
            </div>
          </div>

          <div>
            <label className="label">Strategy</label>
            <select {...register('strategy')} className="input">
              <option value="ringall">Ring All</option>
              <option value="hunt">Hunt</option>
              <option value="memoryhunt">Memory Hunt</option>
              <option value="random">Random</option>
            </select>
          </div>

          <div>
            <label className="label">Ring Time (seconds)</label>
            <input
              type="number"
              {...register('ring_time')}
              className="input"
              placeholder="20"
            />
          </div>
        </div>

        <div className="card space-y-5">
          <h2 className="text-lg font-semibold text-white border-b border-slate-800 pb-3">
            Members ({selectedMembers.length})
          </h2>

          <div className="min-h-[60px] p-3 rounded-lg bg-slate-800/50 border border-slate-700">
            {selectedMembers.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {selectedMembers.map((member) => {
                  const ext = extensions.find(e => e.extension?.toString() === member.toString());
                  return (
                    <span key={member} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary-500/20 text-primary-400 text-sm">
                      {ext?.name || member} ({member})
                      <button type="button" onClick={() => removeMember(member)} className="hover:text-red-400">
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    </span>
                  );
                })}
              </div>
            ) : (
              <p className="text-slate-500 text-sm">No members selected</p>
            )}
          </div>

          <div>
            <label className="label">Add Members</label>
            <div className="max-h-48 overflow-y-auto rounded-lg border border-slate-700 divide-y divide-slate-800">
              {extensions
                .filter(ext => !selectedMembers.includes(ext.extension?.toString()))
                .map((ext) => (
                  <button
                    key={ext.extension}
                    type="button"
                    onClick={() => addMember(ext.extension?.toString())}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-800/50"
                  >
                    <span className="text-slate-300">{ext.name} ({ext.extension})</span>
                    <PlusIcon className="w-4 h-4 text-primary-400" />
                  </button>
                ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <button type="button" onClick={() => navigate('/ring-groups')} className="btn-secondary">
            Cancel
          </button>
          <button type="submit" disabled={isLoading} className="btn-primary">
            {isLoading ? 'Saving...' : isEditing ? 'Update' : 'Create'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RingGroupFormPage;
